/* eslint-disable no-param-reassign */
import React, { useContext, useEffect, useState } from 'react';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import proptypes from 'prop-types';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  client,
  extend,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import { Link, Radio } from '@one-thd/sui-atomic-components';
import {
  dataModel as deliveryDataModel,
  DeliveryOptions
} from '@thd-olt-component-react/delivery-options';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { FulfillmentPodContext } from '../FulfillmentPod';
import { getShippingTemplate } from '../helpers/shipping-helper';
import { resolveSelectedFulfillmentMethod } from '../helpers/select-fulfillment';
import {
  isBODFS,
  isBrioProduct,
  isCustomKitchenCabinetProduct,
  isPodShippingEqual,
  isSTH,
  getFSDBMProgram
} from '../helpers/pod-fulfillment-utils';
import { getStateFromZipcode } from '../helpers/zipcode-helper';
import { EmptyShippingMessage } from '../partials/shipping/EmptyShippingMessage';
import { PriorityDeliveryOptions } from '../partials/shipping/PriorityDeliveryOptions';
import { FSDBMMessage } from '../partials/shipping/FSDBMMessage';
import {
  getDeliveryZip,
  getCheckAvailabilityText,
  getCheckAvailabilityDate
} from '../../components/helper/utils';
import { renderDeliveryTemplates } from '../templates/deliveryTemplate';
import { getEdgeCase } from './fulfillmentPodHelper';

const { bool, string, object, func } = proptypes;

export const FulfillmentPodShipping = React.memo(({
  itemId,
  storeId: primaryStoreId = undefined,
  deliveryZip = undefined,
  useCustomTemplate = false,
  directData = null,
  hasSameDayFilter = false,
  showBundleFulfillmentMessage = false,
  showFasterPodSpacing,
  isNarrowPod = false,
  showFSDBMMessage = false,
  isAppliancePod = false,
  isSelectable = false,
  selectedFulfillmentMethod = null,
  onFulfillmentMethodSelect = null,
  bulkPreference = null
}) => {
  const podContext = useContext(FulfillmentPodContext) || {};
  const { storeId: secondaryStoreId } = useStore();
  const { customer } = useContext(ExperienceContext);
  const [product, setProduct] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const theme = useTheme(FulfillmentPodShipping);
  const enableApplianceDeliveryCharge = useConfigService('enableApplianceDeliveryCharge');
  const enableFreeDeliveryForExchange = useConfigService('enableFreeDeliveryForExchange');
  const applianceDeliveryThreshold = useConfigService('applianceDeliveryThreshold');
  const enableDoubleDeliveryMessage = !!useConfigService('enableDoubleDeliveryMessage');
  // Needed only so a bulk "set all to pickup" can resolve this item's pickup method consistently.
  const isHideLimitedStockClearanceEnabled = !!useConfigService('fs:isHideLimitedStockClearanceEnabled');
  // hide fsdbm message if hideFasterDelivery is true
  const fsdbm = !theme?.props?.hideFasterDelivery && getFSDBMProgram(product);
  const hideFasterDelivery = theme?.props?.hideFasterDelivery || (showFSDBMMessage && fsdbm?.threshold);
  const hideDeliveryEta = theme?.props?.hideDeliveryEta;
  const isNewFulfillmentPodMessagingEnabled = !hideDeliveryEta && !!useConfigService('fs:enableNewFulfillmentPodMessaging');
  const enablePLPFasterDeliveryOption = !!useConfigService('fs:enablePLPFasterDeliveryOption');

  const { isExchangeCustomer } = useThdCustomer() || {};
  const isPuertoRicoZip = !!(getStateFromZipcode(getDeliveryZip()) === 'PR');
  const storeId = primaryStoreId || secondaryStoreId;

  const opts = {
    variables: {
      itemId,
      storeId,
      zipCode: deliveryZip
    },
    skip: !itemId || !!directData,
    ssr: false
  };

  const checkAvailabilityOptions = {
    ssr: false,
    fetchPolicy: 'no-cache'
  };

  const { data, loading, error } = useDataModel('product', opts);
  const productData = data?.product || directData;
  const [
    fetchCheckAvailability,
    { data: checkAvailabilityData, loading: isLoadingAvailability }
  ] = useLazyDataModel('checkAvailability', checkAvailabilityOptions);

  useEffect(() => {
    if (!checkAvailabilityData) return;

    const fulfillmentOptions = checkAvailabilityData?.checkAvailability?.fulfillmentOptions;
    const services = fulfillmentOptions?.[0]?.services;
    const locations = services?.[0]?.locations;

    let earliestAvailabilityDate = getCheckAvailabilityDate(services);

    const response = {
      DeliveryAvailabilityResponse: {
        deliveryAvailability: {
          zipCode: getDeliveryZip(),
          primaryStrNbr: locations?.[0]?.locationId,
          availability: services?.[0]?.deliveryAvailabilityDetail,
          earliestAvailabilityDate
        }
      }
    };

    setAvailabilityData(response);

  }, [checkAvailabilityData]);

  useEffect(() => {
    if (productData) {
      setProduct(productData);
      setAvailabilityData(null);
    }
  }, [productData]);

  const edgeCaseToReturn = getEdgeCase({
    data, error, isLoadingAvailability, loading, product
  });
  if (edgeCaseToReturn) return edgeCaseToReturn;
  const bundleFlag = !!product?.bundleFlag;
  const bundleItemsIds = product?.bundleItems?.map((item) => item.id) || [];

  const nullConditions = [
    product?.info?.productSubType?.name === 'HDQC',
    isBrioProduct(product),
    isCustomKitchenCabinetProduct(product),
    !!product?.installServices?.scheduleAMeasure
  ];

  if (nullConditions.some((nullCondition) => nullCondition)) {
    return null;
  }
  const applianceButtonText = getCheckAvailabilityText({ enablePLPFasterDeliveryOption });

  const checkAvailabilityButton = (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <Link
      data-component="checkAvailabilityButton"
      underline="always"
      onClick={() => {
        fetchCheckAvailability({
          variables: {
            itemIds: bundleFlag && bundleItemsIds.length > 0 ? bundleItemsIds : [itemId],
            zipCode: getDeliveryZip(),
            localStoreId: storeId
          }
        });
      }}
    >
      <span className="sui-text-sm">{applianceButtonText}</span>
    </Link>
  );

  const customRender = useCustomTemplate ? renderDeliveryTemplates : null;
  const ShippingComponent = getShippingTemplate(
    {
      availabilityData,
      product,
      customer,
      checkAvailabilityButton,
      isPuertoRicoZip,
      customRender,
      enableApplianceDeliveryCharge,
      enableFreeDeliveryForExchange,
      applianceDeliveryThreshold,
      isExchangeCustomer,
      bundleFlag,
      showBundleFulfillmentMessage,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod,
      isAppliancePod
    }
  );
  if (!ShippingComponent) { return EmptyShippingMessage; }

  let BodfsComponent = null;
  const bodfsConditions = [
    enableDoubleDeliveryMessage,
    hasSameDayFilter,
    isSTH(product),
    isBODFS(product)
  ];
  if (bodfsConditions.every((condition) => !!condition)) {
    BodfsComponent = getShippingTemplate({
      availabilityData,
      product,
      customer,
      checkAvailabilityButton,
      isPuertoRicoZip,
      customRender,
      enableApplianceDeliveryCharge,
      enableFreeDeliveryForExchange,
      applianceDeliveryThreshold,
      isExchangeCustomer,
      hasSameDayFilter,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod,
      isAppliancePod
    });
  }

  // The fulfillment method this outcome implies (null for disabled/empty outcomes).
  const fulfillmentMethod = ShippingComponent?.fulfillmentMethod ?? null;
  const showSelector = isSelectable && !!fulfillmentMethod;
  // Only resolve when a radio is actually shown, so pods for consumers that don't opt into
  // selection never touch the shared resolver and behave exactly as before.
  const isChecked = showSelector && resolveSelectedFulfillmentMethod(product, {
    selectedMethod: selectedFulfillmentMethod,
    bulkPreference,
    storeId,
    hasSameDayFilter,
    isHideLimitedStockClearanceEnabled
  }) === fulfillmentMethod;

  // PodMessage adds a top margin to the Delivery row (sui-mt-2 legacy, sui-mt-[2px] under
  // enablePLPFasterDeliveryOption) that the pickup row doesn't have. Mirror it on the radio so it
  // stays vertically aligned with the delivery text. Keep this in sync with PodMessage.jsx.
  const radioAlignmentMargin = enablePLPFasterDeliveryOption ? 'sui-mt-[2px]' : 'sui-mt-2';

  const shippingComponentEl = (
    <ShippingComponent
      availabilityData={availabilityData}
      product={product}
      checkAvailabilityButton={checkAvailabilityButton}
      isPuertoRicoZip={isPuertoRicoZip}
      enableApplianceDeliveryCharge={enableApplianceDeliveryCharge}
      enableFreeDeliveryForExchange={enableFreeDeliveryForExchange}
      isExchangeCustomer={isExchangeCustomer}
      bundleFlag={bundleFlag}
      showBundleFulfillmentMessage={showBundleFulfillmentMessage}
      isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
    />
  );

  const content = (
    <span className="sui-leading-tight" data-component="FulfillmentPodShipping">
      {showSelector ? (
        <span className="sui-flex sui-items-start sui-gap-1">
          {/* Negative margins pull the radio bubble flush-left to line up with the icon column.
                The sui Radio nests two layers of left padding — the root span's sui-pl-1 (4px)
                plus the icon span's sui-p-1 (4px) — so -sui-ml-2 (-8px) cancels both; -sui-mr-1
                trims the outer right padding. radioAlignmentMargin matches PodMessage's
                Delivery-row top margin. */}
          <span className={`sui-flex -sui-ml-2 -sui-mr-1 ${radioAlignmentMargin}`}>
            <Radio
              value={fulfillmentMethod}
              name={`fulfillment-method-${itemId}`}
              checked={isChecked}
              onChange={() => { if (onFulfillmentMethodSelect) onFulfillmentMethodSelect(fulfillmentMethod); }}
              inputAttributes={{ 'aria-label': `Select delivery fulfillment: ${fulfillmentMethod}` }}
            />
          </span>
          {shippingComponentEl}
        </span>
      ) : shippingComponentEl}
      {BodfsComponent && (
        <BodfsComponent
          availabilityData={availabilityData}
          product={product}
          checkAvailabilityButton={checkAvailabilityButton}
          isPuertoRicoZip={isPuertoRicoZip}
          enableApplianceDeliveryCharge={enableApplianceDeliveryCharge}
          enableFreeDeliveryForExchange={enableFreeDeliveryForExchange}
          isExchangeCustomer={isExchangeCustomer}
          isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
          isNarrowPod
        />
      )}
      {showFSDBMMessage && fsdbm?.threshold && <FSDBMMessage threshold={fsdbm.threshold} />}
      {!hideFasterDelivery && enablePLPFasterDeliveryOption && (
        <PriorityDeliveryOptions
          product={product}
          showFasterPodSpacing={showFasterPodSpacing}
          isNarrowPod={isNarrowPod}
        />
      )}
    </span>
  );

  // When a radio is shown it takes the place of the delivery icon, so hide the icon (via context)
  // for that outcome only. Disabled/unavailable outcomes render no radio and keep their icon —
  // including the "unavailable" (X) icon. Consumers that don't opt into selection are untouched.
  return showSelector
    ? (
      <FulfillmentPodContext.Provider value={{ ...podContext, hideIcon: true }}>
        {content}
      </FulfillmentPodContext.Provider>
    )
    : content;

}, isPodShippingEqual);

FulfillmentPodShipping.themeProps = {
  hideFasterDelivery: bool,
  hideDeliveryEta: bool
};

FulfillmentPodShipping.defaultThemeProps = {
  hideFasterDelivery: false,
  hideDeliveryEta: false
};

FulfillmentPodShipping.propTypes = {
  itemId: string.isRequired,
  storeId: string,
  useCustomTemplate: bool,
  deliveryZip: string,
  // eslint-disable-next-line react/forbid-prop-types
  directData: object,
  hasSameDayFilter: bool,
  showBundleFulfillmentMessage: bool,
  isNarrowPod: bool,
  showFSDBMMessage: bool,
  isAppliancePod: bool,
  isSelectable: bool,
  selectedFulfillmentMethod: string,
  onFulfillmentMethodSelect: func,
  bulkPreference: string
};

const installServices = params({ storeId: stringType(), zipCode: stringType() }).shape({
  scheduleAMeasure: boolType()
});
if (installServices.skip) {
  installServices.skip('skipInstallServices', true);
}

const Service = shapeType({
  deliveryTimeline: stringType(),
  deliveryDates: shapeType({
    startDate: stringType(),
    endDate: stringType()
  }),
  deliveryCharge: stringType(),
  dynamicEta: shapeType({
    hours: stringType(),
    minutes: stringType()
  }),
  hasFreeShipping: boolType(),
  deliveryMessage: stringType(),
  freeDeliveryThreshold: numberType({ float: true }),
  earliestDeliveryDate: stringType(),
  locations: arrayOfType(shapeType({
    curbsidePickupFlag: boolType(),
    isBuyInStoreCheckNearBy: boolType(),
    distance: numberType({ float: true }),
    inventory: shapeType({
      isOutOfStock: boolType(),
      isInStock: boolType(),
      isLimitedQuantity: boolType(),
      isUnavailable: boolType(),
      quantity: numberType(),
      maxAllowedBopisQty: numberType(),
      minAllowedBopisQty: numberType()
    }),
    isAnchor: boolType(),
    locationId: stringType(),
    state: stringType(),
    storeName: stringType(),
    storePhone: stringType(),
    type: stringType()
  })),
  shipFromFastestLocation: boolType(),
  type: stringType(),
  totalCharge: numberType({ float: true }),
  optimalFulfillment: boolType()
});

FulfillmentPodShipping.dataModel = extend({
  product: params({
    itemId: stringType().isRequired(),
    dataSource: stringType()
  })
    .shape({
      itemId: stringType(),
      bundleFlag: boolType(),
      dataSources: stringType(),
      identifiers: shapeType({
        productType: stringType()
      }),
      info: shapeType({
        classNumber: stringType(),
        hasSubscription: boolType(),
        isLiveGoodsProduct: boolType(),
        productDepartment: stringType(),
        productSubType: shapeType({
          name: stringType()
        }),
        globalCustomConfigurator: shapeType({
          customExperience: stringType()
        }),
        isBrioSku: boolType(),
        isCustomKitchenCabinet: boolType(),
        isGenericProduct: boolType()
      }),
      pricing: params({ storeId: stringType() })
        .shape({
          original: numberType({ float: true }),
          value: numberType({ float: true })
        }),
      availabilityType: shapeType({
        buyable: boolType(),
        discontinued: boolType(),
        status: boolType(),
        type: stringType()
      }),
      fulfillment: client(params({
        storeId: stringType(), zipCode: stringType()
      }).shape({
        anchorStoreStatus: boolType(),
        anchorStoreStatusType: stringType(),
        backordered: boolType(),
        backorderedShipDate: stringType(),
        bossExcludedShipStates: stringType(),
        sthExcludedShipState: stringType(),
        excludedShipStates: stringType(),
        seasonStatusEligible: boolType(),
        fulfillmentOptions: arrayOfType(shapeType({
          type: stringType(),
          fulfillable: boolType(),
          services: arrayOfType(Service),
          priorityDeliveryOptions: arrayOfType(shapeType({
            date: stringType(),
            timeline: stringType(),
            totalCharge: numberType({ float: true })
          })),
          programInclusions: arrayOfType(shapeType({
            description: stringType(),
            threshold: stringType()
          }))
        })),
        onlineStoreStatus: boolType(),
        onlineStoreStatusType: stringType()
      })),
      installServices
    }),
  checkAvailability: params({
    itemIds: arrayOfType(stringType().isRequired()).isRequired(),
    zipCode: stringType().isRequired(),
    localStoreId: stringType().isRequired()
  })
    .shape({
      fulfillmentOptions: arrayOfType(shapeType({
        fulfillable: boolType(),
        services: arrayOfType(
          shapeType({
            type: stringType(),
            isDefault: boolType(),
            earliestAvailabilityDate: stringType(),
            locations: arrayOfType(
              shapeType({
                locationId: stringType()
              })
            ),
            deliveryAvailabilityDetail: arrayOfType(shapeType({
              status: stringType(),
              itemId: stringType()
            }))
          })
        )
      }))
    }),
  shipping: deliveryDataModel.shipping
}, DeliveryOptions);

FulfillmentPodShipping.displayName = 'FulfillmentPodShipping';
